import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

// Import components from Competitions folder
import { CompetitionForm, CompetitionCard, emptyForm, COMPETITION_TYPES } from './Competitions';
import { ScheduleSettingsDialog } from './Competitions/ScheduleSettingsDialog';

// Types that require schedule settings
const TYPES_REQUIRING_SCHEDULE_SETTINGS = ['Local', 'Regional', 'Invitational'];

export default function CompetitionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [competitions, setCompetitions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleSettingsOpen, setScheduleSettingsOpen] = useState(false);
  const [scheduleSettingsReadOnly, setScheduleSettingsReadOnly] = useState(false);
  const [pendingCompetition, setPendingCompetition] = useState(null);
  
  // Reference data
  const [venues, setVenues] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [activityMap, setActivityMap] = useState({});

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`, { withCredentials: true });
      setCompetitions(response.data);
      // Batch-fetch recent activity for the visible comps in a single request
      const ids = (response.data || []).map(c => c.competition_id).filter(Boolean);
      if (ids.length) {
        try {
          const act = await axios.post(
            `${API}/competitions/recent-activity`,
            { competition_ids: ids },
            { withCredentials: true }
          );
          setActivityMap(act.data || {});
        } catch (e) {
          // Non-blocking — the card just renders without the pill.
          console.warn('recent-activity fetch failed', e);
        }
      }
    } catch (error) {
      console.error('Error fetching competitions:', error);
    }
  };
  
  const fetchReferenceData = async () => {
    try {
      const [venuesRes, clubsRes, schedulesRes, usersRes] = await Promise.allSettled([
        axios.get(`${API}/venues`, { withCredentials: true }),
        axios.get(`${API}/clubs`, { withCredentials: true }),
        axios.get(`${API}/content/schedules`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      
      if (venuesRes.status === 'fulfilled') setVenues(venuesRes.value.data);
      if (clubsRes.status === 'fulfilled') setClubs(clubsRes.value.data.sort((a, b) => a.name.localeCompare(b.name)));
      if (schedulesRes.status === 'fulfilled') setSchedules(schedulesRes.value.data);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };
  
  useEffect(() => {
    fetchCompetitions();
    fetchReferenceData();
  }, []);
  
  // Check for action=create query param and auto-open the form
  useEffect(() => {
    if (searchParams.get('action') === 'create') {
      handleOpenCreate();
      setSearchParams({});
    }
  }, [searchParams]);
  
  const resetForm = () => {
    setFormData({ ...emptyForm });
    setEditMode(false);
    setSelectedCompetition(null);
  };
  
  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };
  
  const populateFormFromCompetition = (competition) => ({
    name: competition.name || '',
    short_name: competition.short_name || '',
    sub_competition_name: competition.sub_competition_name || '',
    host_organisation_id: competition.host_organisation_id || '',
    competition_owner_id: competition.competition_owner_id || '',
    venue_id: competition.venue_id || '',
    competition_type: competition.competition_type || 'Local',
    start_date: competition.start_date || '',
    end_date: competition.end_date || '',
    registration_deadline: competition.registration_deadline || '',
    status: competition.status || 'Draft',
    schedule_id: competition.schedule_id || '',
    sub_competition_schedule_id: competition.sub_competition_schedule_id || '',
    figure_events_included: competition.figure_events_included || false,
    artistic_events_included: competition.artistic_events_included || false,
    team_fee_type: competition.team_fee_type || 'standard_individual',
    fee_competitive_base: competition.fee_competitive_base || '',
    fee_competitive_per_event: competition.fee_competitive_per_event || '',
    fee_competitive_max: competition.fee_competitive_max || '',
    fee_team_skater_base: competition.fee_team_skater_base || '',
    fee_team_skater_per_event: competition.fee_team_skater_per_event || '',
    fee_team_skater_max: competition.fee_team_skater_max || '',
    fee_team_base: competition.fee_team_base || '',
    fee_beginner_base: competition.fee_beginner_base || '',
    fee_beginner_per_event: competition.fee_beginner_per_event || '',
    fee_beginner_max: competition.fee_beginner_max || '',
    fee_other_type: competition.fee_other_type || '',
    fee_other_base: competition.fee_other_base || '',
    fee_other_per_event: competition.fee_other_per_event || '',
    fee_other_max: competition.fee_other_max || '',
    music_email: competition.music_email || '',
    music_submission_deadline: competition.music_submission_deadline || '',
    bank_account: competition.bank_account || '',
    bank_account_name: competition.bank_account_name || '',
    bank_account_confirmed: competition.bank_account_confirmed || false,
    registration_instructions: competition.registration_instructions || '',
    public_results: competition.public_results !== false,
    live_streaming: competition.live_streaming || false,
    special_requirements: competition.special_requirements || '',
    nz_champs_country: competition.nz_champs_country || '',
    nz_champs_region_city: competition.nz_champs_region_city || ''
  });
  
  const handleOpenEdit = (competition) => {
    setFormData(populateFormFromCompetition(competition));
    setSelectedCompetition(competition);
    setEditMode(true);
    setDialogOpen(true);
  };
  
  const handleOpenView = (competition) => {
    setFormData(populateFormFromCompetition(competition));
    setSelectedCompetition(competition);
    setViewDialogOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Block re-entry so a double-click on the submit button (or a stray
    // second Enter press) can't POST twice — that was creating duplicate
    // competitions (Feb 2026 bug).
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editMode && selectedCompetition) {
        await axios.patch(`${API}/competitions/${selectedCompetition.competition_id}`, formData, { withCredentials: true });
        toast.success('Competition updated!');
        setDialogOpen(false);
        
        // For types requiring schedule settings, open the schedule settings dialog
        if (TYPES_REQUIRING_SCHEDULE_SETTINGS.includes(formData.competition_type)) {
          // Use the updated competition data
          setPendingCompetition({
            ...selectedCompetition,
            ...formData
          });
          setScheduleSettingsOpen(true);
        } else {
          resetForm();
          fetchCompetitions();
        }
      } else {
        // Create the competition
        const response = await axios.post(`${API}/competitions`, formData, { withCredentials: true });
        const newCompetition = response.data;
        
        // Check if this type requires schedule settings
        if (TYPES_REQUIRING_SCHEDULE_SETTINGS.includes(formData.competition_type)) {
          // Close the create dialog and open schedule settings
          setDialogOpen(false);
          setPendingCompetition(newCompetition);
          setScheduleSettingsOpen(true);
        } else {
          // Standard flow - just complete creation
          toast.success('Competition created!');
          setDialogOpen(false);
          resetForm();
          fetchCompetitions();
        }
      }
    } catch (error) {
      toast.error(editMode ? 'Failed to update competition' : 'Failed to create competition');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleScheduleSettingsComplete = () => {
    toast.success(editMode ? 'Competition updated!' : 'Competition created!');
    resetForm();
    setPendingCompetition(null);
    fetchCompetitions();
  };
  
  const handleDelete = async (id) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };
  
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/competitions/${deleteTargetId}`, { withCredentials: true });
      toast.success('Competition deleted');
      fetchCompetitions();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error('Failed to delete competition');
    } finally {
      setDeleting(false);
      setDeleteTargetId(null);
    }
  };
  
  // Calculate default dates when start date changes
  const handleStartDateBlur = () => {
    if (formData.start_date) {
      const updates = {};
      
      if (!formData.end_date) {
        const startDateObj = new Date(formData.start_date);
        const endDateObj = new Date(startDateObj);
        endDateObj.setDate(endDateObj.getDate() + 1);
        updates.end_date = endDateObj.toISOString().split('T')[0];
      }
      
      if (!formData.registration_deadline) {
        const startDateObj = new Date(formData.start_date);
        const oneMonthBefore = new Date(startDateObj);
        oneMonthBefore.setMonth(oneMonthBefore.getMonth() - 1);
        const dayOfWeek = oneMonthBefore.getDay();
        const daysToSubtract = dayOfWeek === 6 ? 0 : (dayOfWeek + 1);
        oneMonthBefore.setDate(oneMonthBefore.getDate() - daysToSubtract);
        updates.registration_deadline = oneMonthBefore.toISOString().split('T')[0];
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
  };
  
  const filteredCompetitions = competitions.filter(comp => 
    comp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (comp.short_name && comp.short_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-4xl font-bold text-[#2D4A3E] mb-2">
          Competition Management
        </h1>
        <p className="text-muted-foreground">Create and manage artistic rollerskating competitions</p>
      </div>
      
      {/* Search and Add New */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search competitions..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="competition-search-input"
          />
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" onClick={handleOpenCreate} data-testid="create-competition-button">
              <Plus className="mr-2 h-4 w-4" />
              Add New
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit Competition' : 'Add Competition'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <CompetitionForm 
                formData={formData}
                setFormData={setFormData}
                readOnly={false}
                venues={venues}
                clubs={clubs}
                schedules={schedules}
                handleStartDateBlur={handleStartDateBlur}
              />
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="btn-primary" disabled={submitting} data-testid="competition-submit-button">
                  {submitting
                    ? 'Saving…'
                    : TYPES_REQUIRING_SCHEDULE_SETTINGS.includes(formData.competition_type)
                      ? (editMode ? 'Save & Edit Schedule Settings' : 'Goto Schedule Settings')
                      : (editMode ? 'Save Changes' : 'Create Competition')
                  }
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Competition Details</DialogTitle>
          </DialogHeader>
          {selectedCompetition && (
            <>
              <CompetitionForm 
                formData={formData}
                setFormData={setFormData}
                readOnly={true}
                venues={venues}
                clubs={clubs}
                schedules={schedules}
                handleStartDateBlur={handleStartDateBlur}
              />
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
                {TYPES_REQUIRING_SCHEDULE_SETTINGS.includes(selectedCompetition.competition_type) && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setViewDialogOpen(false);
                      setPendingCompetition(selectedCompetition);
                      setScheduleSettingsReadOnly(true);
                      setScheduleSettingsOpen(true);
                    }}
                    data-testid="view-schedule-settings-button"
                  >
                    View Schedule Settings
                  </Button>
                )}
                <Button 
                  className="btn-primary" 
                  onClick={() => { setViewDialogOpen(false); handleOpenEdit(selectedCompetition); }}
                >
                  Edit Competition
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Competition Cards Grid - 2 per row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCompetitions.map(comp => (
          <CompetitionCard 
            key={comp.competition_id}
            competition={comp}
            venues={venues}
            activity={activityMap[comp.competition_id]}
            onView={handleOpenView}
            onEdit={handleOpenEdit}
            onDelete={handleDelete}
          />
        ))}
      </div>
      
      {filteredCompetitions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            {searchTerm ? 'No competitions match your search.' : 'No competitions yet. Create your first one!'}
          </p>
        </div>
      )}
      
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDelete}
        itemName="this competition"
        loading={deleting}
      />
      
      <ScheduleSettingsDialog
        open={scheduleSettingsOpen}
        onOpenChange={(open) => {
          setScheduleSettingsOpen(open);
          if (!open) {
            const wasReadOnly = scheduleSettingsReadOnly;
            setScheduleSettingsReadOnly(false);
            if (!wasReadOnly && pendingCompetition) {
              // If closed without completing edit/create, refresh list
              fetchCompetitions();
            }
            setPendingCompetition(null);
          }
        }}
        competition={pendingCompetition}
        onComplete={handleScheduleSettingsComplete}
        readOnly={scheduleSettingsReadOnly}
      />
    </div>
  );
}
